/**
 * CustomEntitiesCard — settings content for managing custom CRM object types.
 *
 * Gated by the `custom_entity_types` feature flag.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Folder, Plus, Trash01 } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { SCOPE_CUSTOM_OBJECTS, useHasScope } from '@/hooks/useHasScope';
import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState, type EmptyStateProps } from '@/lib/base/empty-state';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { CARD_BORDER } from '@/lib/base/list-card';
import { Table } from '@/lib/base/table';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import type { CrmObjectTypeSchema } from '@/lib/crm/core/SchemaClient';
import { createSchemaClient } from '@/lib/crm/core/SchemaClient';
import { entityTypeHasPipeline } from '@/lib/crm/hooks/useStages';
import { CreateObjectTypeWizard } from '@/lib/crm/list/components/CreateObjectTypeWizard';
import DeleteEntityTypeModal from '@/lib/crm/list/components/DeleteEntityTypeModal';
import type { EntityTypeData } from '@/lib/crm/list/components/EditEntityTypeSlideout';
import { invalidateSchemaCaches } from '@/lib/crm/query';
import { toKebabSlug } from '@/lib/crm/utils/entityTypeUtils';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { logger } from '@/lib/logger';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { useOrg } from '../../../../contexts/OrgContext';

const SYSTEM_CRM_TYPES = new Set([
  'ahoy_contact',
  'ahoy_company',
  'ahoy_deal',
  'ahoy_note',
]);

// ============================================================================
// EntityTypeRow
// ============================================================================

interface EntityTypeRowProps {
  entity: CrmObjectTypeSchema;
  hasPipeline: boolean;
  canDeleteEntityType: boolean;
  onDelete: (entityTypeName: string) => void;
}

function EntityTypeRow({
  entity,
  hasPipeline,
  canDeleteEntityType,
  onDelete,
}: EntityTypeRowProps) {
  const IconComponent = getEntityTypeIconComponent(
    entity.entity_type_name,
    entity.icon ?? undefined,
    entity.entity_kind
  );

  const handlePress = useCallback(
    () => onDelete(entity.entity_type_name),
    [onDelete, entity.entity_type_name]
  );

  return (
    <Table.Row id={entity.entity_type_name}>
      <Table.Cell>
        <div className='flex items-center gap-3'>
          <div className='flex h-8 w-8 items-center justify-center rounded-lg bg-bg-secondary'>
            <IconComponent className='h-4 w-4 text-fg-tertiary' />
          </div>
          <span className='text-sm font-medium text-fg-primary'>
            {entity.label}
          </span>
        </div>
      </Table.Cell>
      <Table.Cell>
        {hasPipeline ? (
          <Badge type='pill-color' size='sm' color='brand'>
            Pipeline
          </Badge>
        ) : (
          <span className='text-sm text-fg-tertiary'>—</span>
        )}
      </Table.Cell>
      <Table.Cell>
        <div className='flex items-center gap-2 justify-end'>
          {canDeleteEntityType && (
            <Tooltip title='Delete object type'>
              <TooltipTrigger
                className='p-1.5 rounded-md text-fg-quaternary hover:text-error-primary hover:bg-error-secondary transition-colors cursor-pointer'
                onPress={handlePress}
              >
                <Trash01 className='h-4 w-4' />
              </TooltipTrigger>
            </Tooltip>
          )}
        </div>
      </Table.Cell>
    </Table.Row>
  );
}

// ============================================================================
// CustomEntitiesCard
// ============================================================================

export function CustomEntitiesCard() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthInfo();
  const isCustomEntityTypesEnabled = useFeatureFlag('custom_entity_types');
  const hasCustomObjects = useHasScope(SCOPE_CUSTOM_OBJECTS);
  // `useHasScope` returns null while the scope is still loading. Treat that
  // loading window as blocked: only surface create actions once the scope has
  // resolved to granted (=== true), and only show the upgrade prompt once it
  // has resolved to absent (=== false). This keeps gating fail-closed so the
  // create UI never flashes before scope validation completes.
  const scopeGranted = hasCustomObjects === true;
  const showUpgrade = useMemo(
    () => isCustomEntityTypesEnabled && hasCustomObjects === false,
    [isCustomEntityTypesEnabled, hasCustomObjects]
  );
  const { hasPermission } = usePermissions();
  const canCreateEntityType = hasPermission(
    'ahoy_entity_type',
    'ahoy_object_created'
  );
  const canDeleteEntityType = hasPermission(
    'ahoy_entity_type',
    'ahoy_object_deleted'
  );

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deletingEntity, setDeletingEntity] = useState<EntityTypeData | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: customEntityTypesData, isLoading } = useQuery({
    queryKey: ['objectTypes', orgId, 'customEntities'],
    queryFn: async () => {
      if (!orgId) {
        return { customEntities: [], allObjectTypes: [] };
      }
      const client = createSchemaClient(orgId);
      const objectTypes = await client.getObjectTypes();
      const customEntities = objectTypes.filter(
        t =>
          t.allows_custom_properties &&
          !SYSTEM_CRM_TYPES.has(t.entity_type_name) &&
          !t.entity_kind.includes('pipeline') &&
          !t.entity_kind.includes('stage')
      );
      return { customEntities, allObjectTypes: objectTypes };
    },
    enabled: !!orgId && isCustomEntityTypesEnabled,
  });

  const customEntities = useMemo(
    () => customEntityTypesData?.customEntities ?? [],
    [customEntityTypesData]
  );
  const allObjectTypes = useMemo(
    () => customEntityTypesData?.allObjectTypes ?? [],
    [customEntityTypesData]
  );

  const { data: entityTypeObjects = [] } = useQuery({
    queryKey: ['crm', orgId, 'objects', 'ahoy_entity_type', 'all'],
    queryFn: async () => {
      if (!orgId) {
        return [];
      }
      const client = createCRMObjectClient(orgId, 'ahoy_entity_type');
      const response = await client.list();
      return response.results;
    },
    enabled: !!orgId && customEntities.length > 0 && isCustomEntityTypesEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const pipelineableMap = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const entity of customEntities) {
      map.set(
        entity.entity_type_name,
        entityTypeHasPipeline(entity.entity_type_name, allObjectTypes)
      );
    }
    return map;
  }, [customEntities, allObjectTypes]);

  const entityTypeDataMap = useMemo(() => {
    const map = new Map<string, EntityTypeData>();
    for (const obj of entityTypeObjects) {
      const typeName = getPropertyValue<string>(obj.properties?.ahoy_type_name);
      if (typeof typeName === 'string') {
        map.set(typeName, {
          entity_id: obj.entity_id,
          entity_type_name: typeName,
          display_name: String(
            getPropertyValue(obj.properties?.ahoy_display_name) ?? ''
          ),
          display_name_plural: String(
            getPropertyValue(obj.properties?.ahoy_display_name_plural) ?? ''
          ),
          icon: String(getPropertyValue(obj.properties?.ahoy_icon) ?? ''),
          pipelineable: pipelineableMap.get(typeName) ?? false, // derived from helper via pipelineableMap
        });
      }
    }
    return map;
  }, [entityTypeObjects, pipelineableMap]);

  const handleCreated = useCallback(
    (entityTypeName: string) => {
      setIsCreateOpen(false);
      if (orgId) {
        navigate(`/${orgId}/crm/${toKebabSlug(entityTypeName)}`);
      }
    },
    [navigate, orgId]
  );

  const handleDelete = useCallback(
    (entityTypeName: string) => {
      const data = entityTypeDataMap.get(entityTypeName);
      if (data) {
        setDeletingEntity(data);
        return;
      }
      const schemaEntry = customEntities.find(
        e => e.entity_type_name === entityTypeName
      );
      if (schemaEntry) {
        setDeletingEntity({
          entity_id: '',
          entity_type_name: entityTypeName,
          display_name: schemaEntry.label,
          display_name_plural: schemaEntry.label_plural,
          icon: schemaEntry.icon ?? '',
          pipelineable: pipelineableMap.get(entityTypeName) ?? false,
        });
      }
    },
    [entityTypeDataMap, customEntities, pipelineableMap]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingEntity || !orgId || !user?.id) {
      return;
    }

    setIsDeleting(true);
    try {
      let entityId = deletingEntity.entity_id;
      const client = createCRMObjectClient(orgId, 'ahoy_entity_type');
      if (!entityId) {
        let page = 1;
        const pageSize = 100;
        while (!entityId) {
          const response = await client.list({ page, page_size: pageSize });
          const match = response.results.find(obj => {
            const typeName = getPropertyValue<string>(
              obj.properties?.ahoy_type_name
            );
            return typeName === deletingEntity.entity_type_name;
          });
          if (match) {
            entityId = match.entity_id;
            break;
          }
          if (!response.next) {
            break;
          }
          page += 1;
        }
        if (!entityId) {
          throw new Error('Entity type object not found');
        }
      }

      const freshObj = await client.get(entityId);

      const builder = createEventBuilder({
        sourceType: 'ahoy_user',
        sourceId: String(user.id),
        startSeq: freshObj.last_event_seq,
      });
      builder.objectDeleted(entityId, 'ahoy_entity_type');

      await client.submitEvents({ events: builder.build() });

      await invalidateSchemaCaches(queryClient, orgId);
      await queryClient.invalidateQueries({
        queryKey: ['crm', orgId, 'objects', 'ahoy_entity_type'],
        exact: false,
      });

      toast.success('Object type deleted');
      setDeletingEntity(null);
    } catch (err) {
      logger.error(
        'Failed to delete entity type',
        err instanceof Error ? err : new Error(String(err)),
        {
          org_id: orgId,
          entity_id: deletingEntity.entity_id,
        }
      );
      toast.error("We couldn't delete the object type. Try again.");
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [deletingEntity, orgId, queryClient, user]);

  const handleOpenCreate = useCallback(() => setIsCreateOpen(true), []);
  const handleCloseCreate = useCallback(() => setIsCreateOpen(false), []);
  const handleClearDeleting = useCallback(() => setDeletingEntity(null), []);
  const handlePressDelete = useCallback(
    (entityName: string) => handleDelete(entityName),
    [handleDelete]
  );
  const handleViewPlans = useCallback(() => {
    if (orgId) {
      navigate(`/${orgId}/settings/billing`);
    }
  }, [navigate, orgId]);

  if (!isCustomEntityTypesEnabled) {
    return null;
  }

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <div className='space-y-3'>
        {[1, 2, 3].map(i => (
          <div key={i} className='h-10 bg-bg-tertiary rounded animate-pulse' />
        ))}
      </div>
    );
  } else if (customEntities.length === 0) {
    let emptyAction: EmptyStateProps['action'];
    if (showUpgrade) {
      emptyAction = {
        label: 'View plans',
        onClick: handleViewPlans,
        color: 'primary',
      };
    } else if (canCreateEntityType) {
      emptyAction = {
        label: 'Create object type',
        onClick: handleOpenCreate,
        icon: Plus,
        color: 'primary',
        isDisabled: !scopeGranted,
        disabledTooltip: 'Upgrade to Pro or Growth to create custom objects',
      };
    }

    content = (
      <div className={cx(CARD_BORDER, 'bg-bg-secondary')}>
        <EmptyState
          icon={
            <FeaturedIcon
              icon={Folder}
              size='lg'
              theme='light-outline'
              color='gray'
            />
          }
          title={
            showUpgrade
              ? 'Upgrade to unlock custom objects'
              : 'No custom objects'
          }
          description={
            showUpgrade
              ? 'Custom objects are available on Pro and Growth plans.'
              : 'Create a custom object type to organize your data'
          }
          action={emptyAction}
        />
      </div>
    );
  } else {
    content = (
      <div className='overflow-hidden rounded-lg border border-border-secondary'>
        <Table aria-label='Custom object types' size='sm'>
          <Table.Header>
            <Table.Head label='Name' isRowHeader />
            <Table.Head label='Pipeline' />
            <Table.Head label='Actions' align='right' />
          </Table.Header>
          <Table.Body>
            {customEntities.map(entity => (
              <EntityTypeRow
                key={entity.entity_type_name}
                entity={entity}
                hasPipeline={
                  pipelineableMap.get(entity.entity_type_name) ?? false
                }
                canDeleteEntityType={canDeleteEntityType}
                onDelete={handlePressDelete}
              />
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4'>
          <h3 className='text-sm font-medium text-fg-primary'>
            Custom objects
          </h3>
          {canCreateEntityType && (
            <Button
              size='sm'
              color='primary'
              iconLeading={Plus}
              onClick={handleOpenCreate}
              isDisabled={!scopeGranted}
              disabledTooltip='Upgrade to Pro or Growth to create custom objects'
              className='self-end sm:self-auto'
            >
              Create object type
            </Button>
          )}
        </div>

        {content}
      </div>

      {orgId && (
        <CreateObjectTypeWizard
          isOpen={isCreateOpen}
          onClose={handleCloseCreate}
          orgId={orgId}
          onCreated={handleCreated}
        />
      )}

      {deletingEntity && orgId && (
        <DeleteEntityTypeModal
          isOpen={!!deletingEntity}
          onClose={handleClearDeleting}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
          entityTypeName={deletingEntity.entity_type_name}
          entityDisplayName={deletingEntity.display_name}
          entityDisplayNamePlural={deletingEntity.display_name_plural}
          orgId={orgId}
          isPipelineable={deletingEntity.pipelineable ?? false}
        />
      )}
    </>
  );
}
